import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGameStore } from '@/store/gameStore';
import { CharacterPortrait } from '@/components/game/CharacterPortrait';
import { StatBadge } from '@/components/game/StatBadge';
import { Button } from '@/components/ui/button';
import { BrandCorner } from '@/components/BrandCorner';
import { subscribeToMatch, subscribeToPlayers, subscribeToBattleRounds, updateMatchStatus } from '@/lib/multiplayer';
import { toPng } from 'html-to-image';
import { playSfx, stopMusic } from '@/lib/audio';
import { toast } from 'sonner';

export function ResultsScreen() {
  const {
    mode,
    matchId,
    player,
    opponent,
    battleHistory,
    auctionHistory,
    resultOverride,
    settings,
    resetGame,
    startRematch,
    playerRole,
    applyMatchSnapshot,
    playerBoosts,
  } = useGameStore();

  const captureRef = useRef<HTMLDivElement | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isRematching, setIsRematching] = useState(false);

  // For PvP, subscribe to battle rounds on mount so the scorecard populates
  // even if battleHistory was empty when we transitioned to results.
  const { applyBattleHistorySnapshot, applyPlayersSnapshot } = useGameStore();
  useEffect(() => {
    if (mode !== 'pvp' || !matchId) return;
    const unsubMatch = subscribeToMatch(matchId, (match) => {
      if (match.status === 'auction' || match.status === 'battle') {
        applyMatchSnapshot(match);
      }
    });
    // Fetch battle rounds for the results screen
    const unsub = subscribeToBattleRounds(matchId, (rounds) => {
      applyBattleHistorySnapshot(rounds);
    });
    // Also subscribe to players so we can derive resultOverride if it's still null.
    // Must call applyPlayersSnapshot so the store gets the authoritative rounds_won
    // from match_players (double-win gives 2 pts for 1 round — history count is wrong).
    // applyPlayersSnapshot already derives resultOverride when phase === 'results'.
    const unsubPlayers = subscribeToPlayers(matchId, (players) => {
      applyPlayersSnapshot(players);
    });
    return () => { unsubMatch(); unsub(); unsubPlayers(); };
  }, [mode, matchId, applyMatchSnapshot, applyBattleHistorySnapshot, applyPlayersSnapshot]);

  const resolvedResult =
    resultOverride ??
    (player.roundsWon > opponent.roundsWon
      ? 'player'
      : player.roundsWon < opponent.roundsWon
      ? 'opponent'
      : 'draw');

  const playerWon = resolvedResult === 'player';
  const isDraw = resolvedResult === 'draw';

  const totalCoinsSpent = auctionHistory
    .filter((r) => r.winner === 'player')
    .reduce((sum, r) => sum + r.playerBid, 0);

  const charsWon = auctionHistory.filter((r) => r.winner === 'player').length;
  const efficiency = charsWon === 0
    ? 1
    : Math.max(1, Math.min(5, 5 - Math.floor(totalCoinsSpent / charsWon / 1000)));

  const { mvp, biggestOverpay, bestRead, clutchText, mostPickedPlayer, mostPickedOpponent } =
    useMemo(() => {
      const winCounts = new Map<string, { name: string; count: number }>();
      battleHistory.forEach((round) => {
        const addWin = (id: string, name: string) => {
          const e = winCounts.get(id) ?? { name, count: 0 };
          winCounts.set(id, { name: e.name, count: e.count + 1 });
        };
        if (round.winner === 'player' && round.playerCharacter)
          addWin(round.playerCharacter.id, round.playerCharacter.name);
        if (round.winner === 'opponent' && round.opponentCharacter)
          addWin(round.opponentCharacter.id, round.opponentCharacter.name);
      });

      let mvpEntry: { name: string; count: number } | null = null;
      winCounts.forEach((v) => {
        if (!mvpEntry || v.count > mvpEntry.count) mvpEntry = v;
      });

      const winBids = auctionHistory
        .filter((r) => r.winner !== 'tie')
        .map((r) => ({
          name: r.character.name,
          amount: r.winner === 'player' ? r.playerBid : r.opponentBid,
          winner: r.winner === 'player' ? 'You' : opponent.name,
        }));

      const biggestOverpay = winBids.length
        ? winBids.reduce((max, x) => (x.amount > max.amount ? x : max), winBids[0])
        : null;
      const bestRead = winBids.length
        ? winBids.reduce((min, x) => (x.amount < min.amount ? x : min), winBids[0])
        : null;

      let pScore = 0, oScore = 0, pDeficit = 0, oDeficit = 0;
      let pStreak = 0, oStreak = 0, pMaxStreak = 0, oMaxStreak = 0;
      let leadChanges = 0, lastLeader: 'p' | 'o' | null = null;
      battleHistory.forEach((r) => {
        pDeficit = Math.max(pDeficit, oScore - pScore);
        oDeficit = Math.max(oDeficit, pScore - oScore);
        if (r.winner === 'player') { pScore++; pStreak++; oStreak = 0; pMaxStreak = Math.max(pMaxStreak, pStreak); }
        if (r.winner === 'opponent') { oScore++; oStreak++; pStreak = 0; oMaxStreak = Math.max(oMaxStreak, oStreak); }
        const leader = pScore > oScore ? 'p' : oScore > pScore ? 'o' : null;
        if (leader && leader !== lastLeader && lastLeader !== null) leadChanges++;
        if (leader) lastLeader = leader;
      });

      const totalRounds = battleHistory.length;
      const winnerDeficit = isDraw ? 0 : playerWon ? pDeficit : oDeficit;
      const winnerMaxStreak = playerWon ? pMaxStreak : oMaxStreak;
      const margin = Math.abs(pScore - oScore);
      const isCleanSweep = (pScore === 0 || oScore === 0) && totalRounds > 0;

      let clutchText: string;
      if (isDraw) {
        const drawMsgs = [
          'Perfectly balanced',
          'Dead even',
          'Mirror match',
          'Stalemate',
          'Neck and neck',
          'Too close to call',
        ];
        clutchText = drawMsgs[Math.floor(Math.random() * drawMsgs.length)];
      } else if (isCleanSweep) {
        const sweepMsgs = [
          'Clean sweep',
          'Total domination',
          'Flawless run',
          'Untouchable',
          'No contest',
        ];
        clutchText = sweepMsgs[Math.floor(Math.random() * sweepMsgs.length)];
      } else if (winnerDeficit >= 3) {
        const epicMsgs = [
          `Epic ${winnerDeficit}-round comeback`,
          `Clawed back from ${winnerDeficit} down`,
          `Reverse swept from ${winnerDeficit}-round hole`,
          `Miracle comeback (${winnerDeficit} down)`,
        ];
        clutchText = epicMsgs[Math.floor(Math.random() * epicMsgs.length)];
      } else if (winnerDeficit > 0) {
        const comebackMsgs = [
          `Came back from ${winnerDeficit}-round deficit`,
          `Rallied from ${winnerDeficit} behind`,
          `Gutsy ${winnerDeficit}-round comeback`,
          `Never gave up (${winnerDeficit} down)`,
        ];
        clutchText = comebackMsgs[Math.floor(Math.random() * comebackMsgs.length)];
      } else if (leadChanges >= 3) {
        const swingMsgs = [
          'Back-and-forth battle',
          `Wild ride (${leadChanges} lead changes)`,
          'Momentum swings',
          'Rollercoaster match',
        ];
        clutchText = swingMsgs[Math.floor(Math.random() * swingMsgs.length)];
      } else if (winnerMaxStreak >= 3) {
        const streakMsgs = [
          `${winnerMaxStreak}-round hot streak`,
          `Went on a ${winnerMaxStreak}-win tear`,
          `Caught fire (${winnerMaxStreak} straight)`,
          `Unstoppable streak (${winnerMaxStreak})`,
        ];
        clutchText = streakMsgs[Math.floor(Math.random() * streakMsgs.length)];
      } else if (margin === 1) {
        const closeMsgs = [
          'Nail-biter finish',
          'Won by a hair',
          'Photo finish',
          'Down to the wire',
          'Squeaked it out',
        ];
        clutchText = closeMsgs[Math.floor(Math.random() * closeMsgs.length)];
      } else {
        const defaultMsgs = [
          'Front-runner',
          'Controlled the pace',
          'Steady and strong',
          'Wire to wire',
          'Led from the start',
        ];
        clutchText = defaultMsgs[Math.floor(Math.random() * defaultMsgs.length)];
      }

      const pickSummary = (
        rounds: typeof battleHistory,
        key: 'playerCharacter' | 'opponentCharacter'
      ) => {
        const map = new Map<string, { name: string; count: number }>();
        rounds.forEach((r) => {
          const c = r[key];
          if (!c) return;
          const e = map.get(c.id) ?? { name: c.name, count: 0 };
          map.set(c.id, { name: e.name, count: e.count + 1 });
        });
        let best: { name: string; count: number } | null = null;
        map.forEach((v) => {
          if (!best || v.count > best.count) best = v;
        });
        return best ? `${best.name} (x${best.count})` : 'N/A';
      };

      return {
        mvp: mvpEntry ? `${mvpEntry.name} (${mvpEntry.count} wins)` : 'N/A',
        biggestOverpay: biggestOverpay
          ? `${biggestOverpay.winner} paid ${biggestOverpay.amount.toLocaleString()} for ${biggestOverpay.name}`
          : 'N/A',
        bestRead: bestRead
          ? `${bestRead.winner} won ${bestRead.name} for ${bestRead.amount.toLocaleString()}`
          : 'N/A',
        clutchText,
        mostPickedPlayer: pickSummary(battleHistory, 'playerCharacter'),
        mostPickedOpponent: pickSummary(battleHistory, 'opponentCharacter'),
      };
    // battleHistory, playerWon, isDraw are the only values clutchText depends on.
    // Using length + join as a stable dep so the message is picked once and frozen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [battleHistory.length, playerWon, isDraw]);

  useEffect(() => {
    stopMusic();
    if (playerWon) playSfx('match_victory');
    else if (!isDraw) playSfx('match_defeat');
  }, [playerWon, isDraw]);

  const handleShareResults = async () => {
    if (!captureRef.current || isExporting) return;
    setIsExporting(true);
    try {
      const dataUrl = await toPng(captureRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        filter: (node) =>
          !(node instanceof HTMLElement) || node.dataset.ignore !== 'true',
      });
      const link = document.createElement('a');
      link.download = 'anime-draft-auction-results.png';
      link.href = dataUrl;
      link.click();
    } catch {
      toast.error('Could not export results image. Try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleQuickRematch = async () => {
    if (isRematching) return;
    if (mode === 'cpu') {
      startRematch();
      return;
    }
    if (!matchId) {
      toast.error('Could not start rematch. Match not found.');
      return;
    }
    if (playerRole !== 'host') {
      toast.info('Host can start the rematch in this room.');
      return;
    }
    setIsRematching(true);
    const result = await updateMatchStatus(matchId, 'auction');
    setIsRematching(false);
    if (!result.ok) {
      toast.error('Could not start rematch. Try again.');
      return;
    }
    toast.success('Rematch started');
  };

  const resultAccent = playerWon ? '#22c55e' : isDraw ? '#f59e0b' : '#ef4444';
  const resultGradient = playerWon
    ? 'from-green-500/20 via-transparent to-green-500/5'
    : isDraw
    ? 'from-amber-500/20 via-transparent to-amber-500/5'
    : 'from-red-500/20 via-transparent to-red-500/5';

  return (
    <div className="min-h-screen bg-arena flex flex-col items-center justify-start p-4 pt-16 md:pt-20 gap-6">
      <BrandCorner />

      {/* ── SHARE CARD ────────────────────────────────────────────── */}
      <div
        ref={captureRef}
        className="w-full max-w-2xl rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, hsl(var(--secondary)) 0%, hsl(var(--background)) 100%)',
          border: `2px solid ${resultAccent}40`,
        }}
      >
        {/* Card header — result strip */}
        <div
          className={`relative px-6 py-5 bg-gradient-to-r ${resultGradient}`}
          style={{ borderBottom: `2px solid ${resultAccent}30` }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-3xl md:text-4xl font-display font-black tracking-tight"
                style={{ color: resultAccent }}
              >
                {playerWon ? 'VICTORY' : isDraw ? 'DRAW' : 'DEFEAT'}
              </h1>
              <p className="text-xs text-muted-foreground font-display mt-1">
                Anime Draft Auction
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">YOU</p>
                <p className="font-mono font-black text-3xl" style={{ color: playerWon ? resultAccent : 'hsl(var(--foreground))' }}>
                  {player.roundsWon}
                </p>
              </div>
              <div className="text-xl text-muted-foreground font-mono">:</div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">{opponent.name.toUpperCase()}</p>
                <p className="font-mono font-black text-3xl" style={{ color: !playerWon && !isDraw ? resultAccent : 'hsl(var(--foreground))' }}>
                  {opponent.roundsWon}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Teams row */}
        <div className="px-6 py-4 flex gap-6">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Your Team</p>
            <div className="flex gap-1.5 flex-wrap">
              {player.team.map((char) => (
                <CharacterPortrait key={char.id} character={char} size="xs" showName={false} />
              ))}
            </div>
          </div>
          <div className="w-px bg-border" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Opponent Team</p>
            <div className="flex gap-1.5 flex-wrap">
              {opponent.team.map((char) => (
                <CharacterPortrait key={char.id} character={char} size="xs" showName={false} />
              ))}
            </div>
          </div>
        </div>

        {/* Battle rounds — compact */}
        {battleHistory.length > 0 && (
          <div className="px-6 pb-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Battle Rounds</p>
            <div className="grid gap-1">
              {battleHistory.map((round, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs"
                  style={{
                    background:
                      round.winner === 'player'
                        ? 'rgba(34,197,94,0.08)'
                        : round.winner === 'opponent'
                        ? 'rgba(239,68,68,0.08)'
                        : 'rgba(255,255,255,0.03)',
                  }}
                >
                  <span className="text-muted-foreground w-5 shrink-0 font-mono">R{i + 1}</span>
                  <StatBadge stat={round.stat} size="sm" />
                  <span className="flex-1 text-right min-w-0">
                    <span className={round.winner === 'player' ? 'text-success font-semibold' : 'text-muted-foreground'}>
                      {round.playerCharacter?.name}
                    </span>
                    <span className="text-muted-foreground/50 mx-1 font-mono">
                      {round.playerStatValue}-{round.opponentStatValue}
                    </span>
                    <span className={round.winner === 'opponent' ? 'text-red-400 font-semibold' : 'text-muted-foreground'}>
                      {round.opponentCharacter?.name}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats strip */}
        <div className="px-6 py-3 flex justify-around border-t border-border/50">
          <div className="text-center">
            <p className="font-mono font-bold text-sm text-primary">{totalCoinsSpent.toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">Coins Spent</p>
          </div>
          <div className="text-center">
            <p className="font-mono font-bold text-sm" style={{ color: resultAccent }}>{player.team.length}</p>
            <p className="text-[10px] text-muted-foreground">Chars Won</p>
          </div>
          <div className="text-center">
            <p className="font-mono font-bold text-sm text-amber-400">{'*'.repeat(efficiency)}</p>
            <p className="text-[10px] text-muted-foreground">Efficiency</p>
          </div>
          <div className="text-center">
            <p className="font-mono font-bold text-sm text-foreground">{clutchText}</p>
            <p className="text-[10px] text-muted-foreground">Momentum</p>
          </div>
        </div>

        {/* Highlights grid */}
        <div className="px-6 py-3 border-t border-border/50">
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'MVP', value: mvp, icon: 'M' },
              { label: 'Best Read', value: bestRead, icon: 'B' },
              { label: 'Biggest Overpay', value: biggestOverpay, icon: 'O' },
              { label: 'Your Pick', value: mostPickedPlayer, icon: 'Y' },
              { label: 'Opp Pick', value: mostPickedOpponent, icon: 'E' },
              { label: 'Verse', value: settings.verse, icon: 'V' },
              (() => {
                // Use *EverUsed flags — the per-round flags (rerollUsed etc.) are
                // reset by the server after each round, but EverUsed latches true
                // permanently so the results card always shows what was purchased.
                const boosts: string[] = [];
                if (playerBoosts.rerollEverUsed) boosts.push('Reroll');
                if (playerBoosts.extraTimeEverUsed) boosts.push('+5s');
                if (playerBoosts.doubleWinEverUsed) boosts.push('Dbl Win');
                if (playerBoosts.statBoostEverUsed) boosts.push(`Boost +${playerBoosts.statBoostPctRecord || playerBoosts.statBoostPct}%`);
                if (playerBoosts.weakenEverUsed) boosts.push(`Weaken -${playerBoosts.weakenPctRecord || playerBoosts.weakenPct}%`);
                return { label: 'Shop Buys', value: boosts.length ? boosts.join(', ') : 'None', icon: 'S' };
              })(),
            ].map(({ label, value, icon }) => (
              <div key={label} className="rounded-lg bg-background/50 border border-border/40 px-3 py-2">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span
                    className="w-4 h-4 rounded-full text-[8px] font-bold flex items-center justify-center shrink-0"
                    style={{ background: `${resultAccent}25`, color: resultAccent }}
                  >
                    {icon}
                  </span>
                  <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</p>
                </div>
                <p className="text-[11px] font-display leading-tight whitespace-normal break-words min-h-[2.4em]">
                  {value}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer watermark */}
        <div className="px-6 py-2 flex justify-between items-center border-t border-border/30">
          <span className="text-[9px] text-muted-foreground/60 font-display">auction.anime-draft.com</span>
          <span className="text-[9px] text-muted-foreground/60 font-mono">
            Bo{settings.battleFormat} | {settings.auctionCount} chars | {settings.startingCoins} coins
          </span>
        </div>
      </div>

      {/* ── ACTION BUTTONS (excluded from share) ─────────────────── */}
      <motion.div
        className="flex flex-wrap gap-4 justify-center"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        data-ignore="true"
      >
        <Button
          size="lg"
          onClick={handleQuickRematch}
          disabled={isRematching || (mode === 'pvp' && playerRole !== 'host')}
          className="h-14 px-8 text-lg font-display font-bold"
        >
          {isRematching ? 'Starting...' : mode === 'pvp' && playerRole !== 'host' ? 'Host Starts Rematch' : 'Quick Rematch'}
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={handleShareResults}
          disabled={isExporting}
          className="h-14 px-8 text-lg font-display"
        >
          {isExporting ? 'Preparing...' : 'Share Results'}
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={resetGame}
          className="h-14 px-8 text-lg font-display"
        >
          Home
        </Button>
      </motion.div>
    </div>
  );
}
